/**
 * ConnectClaudeCodeModal -- "Connect my Claude Code" (FEAT-033, TASK-011, design.md D.2).
 *
 * Two lines of explanation, the shared ConnectSnippets in MASKED mode (Claude Code tab active by
 * default) and "Open my API key" -> /settings?tab=api-key. No secret is fetched or shown
 * here: the only data call is useMyApiKeys (name / prefix / status), and it runs only while the
 * modal is open (the body is mounted only when open, so hosts that never open it -- and their
 * tests -- make no query at all).
 */

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import { Key, Plus } from '@phosphor-icons/react';
import { Alert, Button, ErrorState, Modal, Skeleton } from '@/components/ui';
import { useMyApiKeys } from '@/hooks/useMyApiKeys';
import { ConnectSnippets } from './ConnectSnippets';

export interface ConnectClaudeCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const API_KEY_PAGE_PATH = '/settings?tab=api-key';
// --- FEAT-056 deep link ---
/** Opens the create flow of the API key tab once; the tab removes `create` from the URL. */
export const API_KEY_CREATE_PARAM = 'create';
export const API_KEY_CREATE_PATH = `${API_KEY_PAGE_PATH}&${API_KEY_CREATE_PARAM}=1`;
// --- /FEAT-056 deep link ---

function ConnectClaudeCodeBody({ onClose }: { onClose: () => void }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const keysQuery = useMyApiKeys({ enabled: true });

  // --- FEAT-056 TASK-007 ---
  const goTo = (path: string) => {
    onClose();
    navigate(path);
  };
  // --- /FEAT-056 TASK-007 ---

  const defaultKey = keysQuery.data?.data.find((k) => k.is_default && k.status !== 'revoked');

  const hasNoKey = Boolean(keysQuery.data) && !defaultKey;

  let snippets: React.ReactNode;
  if (keysQuery.isLoading) {
    snippets = <Skeleton variant="rect" className="h-48 rounded-lg" />;
  } else if (keysQuery.isError || !keysQuery.data) {
    snippets = (
      <ErrorState
        message={intl.formatMessage({ id: 'settings.apiKey.error.load' })}
        onRetry={() => void keysQuery.refetch()}
        className="py-6"
      />
    );
  } else if (!defaultKey) {
    snippets = <Alert variant="info">{intl.formatMessage({ id: 'settings.apiKey.connectModal.noKey' })}</Alert>;
  } else {
    snippets = (
      <ConnectSnippets
        keyPrefix={defaultKey.key_prefix}
        secret={null}
        baseUrls={keysQuery.data.base_urls}
        defaultClient="claude_code"
        idPrefix="connect-modal-snippets"
        // --- FEAT-056 TASK-007 ---
        onCreateOrRotate={() => goTo(API_KEY_PAGE_PATH)}
        // --- /FEAT-056 TASK-007 ---
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <p className="text-sm text-text-secondary">{intl.formatMessage({ id: 'settings.apiKey.connectModal.line1' })}</p>
        <p className="text-sm text-text-secondary">{intl.formatMessage({ id: 'settings.apiKey.connectModal.line2' })}</p>
      </div>
      {snippets}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="ghost" onClick={onClose}>
          {intl.formatMessage({ id: 'common.close' })}
        </Button>
        {/* --- FEAT-056 TASK-007 --- */}
        {hasNoKey ? (
          <Button variant="primary" icon={<Plus size={16} aria-hidden="true" />} onClick={() => goTo(API_KEY_CREATE_PATH)}>
            {intl.formatMessage({ id: 'settings.apiKey.empty.action' })}
          </Button>
        ) : (
          <Button variant="primary" icon={<Key size={16} aria-hidden="true" />} onClick={() => goTo(API_KEY_PAGE_PATH)}>
            {intl.formatMessage({ id: 'settings.apiKey.connectModal.openKeyPage' })}
          </Button>
        )}
        {/* --- /FEAT-056 TASK-007 --- */}
      </div>
    </div>
  );
}

export function ConnectClaudeCodeModal({ isOpen, onClose }: ConnectClaudeCodeModalProps) {
  const intl = useIntl();
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      title={intl.formatMessage({ id: 'settings.apiKey.connectModal.title' })}
    >
      {isOpen && <ConnectClaudeCodeBody onClose={onClose} />}
    </Modal>
  );
}

export default ConnectClaudeCodeModal;
